import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, ShieldCheck, KeyRound, Mail, LogOut } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import {
  isTwoFactorVerified,
  markTwoFactorVerified,
  requestTwoFactorCode,
  useTwoFactor,
  verifyTwoFactorCode,
} from "@/hooks/useTwoFactor";

const RESEND_SECONDS = 45;

/**
 * Full-screen 2FA step shown right after sign-in when the account requires it.
 * Nothing else of the app is visible until verification succeeds.
 */
export default function TwoFactorGate() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { state, loading } = useTwoFactor();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const requestedFor = useRef<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const send = useCallback(async (silent = false) => {
    setSending(true);
    try {
      const result = await requestTwoFactorCode();
      if (result?.simulated) {
        toast.info(
          result.dev_code
            ? `${t("security.gate.simulated")} ${result.dev_code}`
            : t("security.gate.simulated"),
        );
      } else if (!silent) {
        toast.success(t("security.gate.sent"));
      }
      setCooldown(RESEND_SECONDS);
    } catch {
      toast.error(t("security.gate.sendError"));
    }
    setSending(false);
  }, []);

  useEffect(() => {
    if (!user?.id || loading) return;
    if (!state.enabled) return;
    if (isTwoFactorVerified(user.id)) return;

    setOpen(true);
    if (requestedFor.current === user.id) return;
    requestedFor.current = user.id;
    void send(true);
  }, [user?.id, loading, state.enabled, send]);

  // Lock scroll and focus the input while the gate is visible
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => inputRef.current?.focus(), 120);
    return () => {
      document.body.style.overflow = prev;
      clearTimeout(t);
    };
  }, [open]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const verify = async (value?: string) => {
    const c = value ?? code;
    if (c.length !== 6 || !user?.id || verifying) return;
    setVerifying(true);
    const result = await verifyTwoFactorCode(c);
    setVerifying(false);
    if (result.verified) {
      markTwoFactorVerified(user.id);
      setOpen(false);
      toast.success(t("security.gate.success"));
    } else {
      setCode("");
      inputRef.current?.focus();
      toast.error(
        result.error === "expired"
          ? t("security.gate.expired")
          : result.error === "too_many_attempts"
            ? t("security.gate.tooMany")
            : t("security.gate.invalid"),
      );
    }
  };

  const cancel = async () => {
    setOpen(false);
    requestedFor.current = null;
    await supabase.auth.signOut();
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[2147483000] bg-background overflow-y-auto"
      role="dialog"
      aria-modal="true"
    >
      {/* Soft brand backdrop */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_-10%,hsl(var(--primary)/0.18),transparent_65%)]" />

      <div className="relative min-h-full flex flex-col items-center justify-center px-5 py-10">
        <div className="w-full max-w-[400px]">
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-3xl bg-primary/10 flex items-center justify-center shadow-[0_10px_30px_-12px_hsl(var(--primary)/0.55)]">
              <ShieldCheck className="w-7 h-7 text-primary" strokeWidth={2} />
            </div>
            <h1 className="mt-5 text-xl sm:text-2xl font-bold tracking-tight text-foreground">
              {t("security.gate.title")}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed max-w-[320px]">
              {state.method === "email"
                ? t("security.gate.descEmail")
                : t("security.gate.descPhone")}
            </p>
          </div>

          <div className="mt-8 rounded-3xl border border-border/60 bg-card p-5 sm:p-6 shadow-[0_20px_50px_-30px_hsl(var(--foreground)/0.35)]">
            <div className="relative">
              <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                ref={inputRef}
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setCode(v);
                  if (v.length === 6) void verify(v);
                }}
                onKeyDown={(e) => e.key === "Enter" && verify()}
                placeholder="000000"
                className="pl-11 h-14 rounded-2xl text-center text-lg tracking-[0.45em] font-mono"
                maxLength={6}
              />
            </div>

            <Button
              className="w-full h-12 rounded-2xl mt-4 font-semibold"
              disabled={code.length !== 6 || verifying}
              onClick={() => verify()}
            >
              {verifying && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {t("security.gate.verify")}
            </Button>

            <div className="mt-4 flex items-center justify-center">
              <Button
                variant="ghost"
                size="sm"
                className="rounded-xl text-xs"
                onClick={() => send()}
                disabled={sending || cooldown > 0}
              >
                {sending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                ) : (
                  <Mail className="w-3.5 h-3.5 mr-1.5" />
                )}
                {cooldown > 0 ? t("security.gate.resendIn", { s: cooldown }) : t("security.gate.resend")}
              </Button>
            </div>
          </div>

          <div className="mt-6 flex flex-col items-center gap-3">
            <p className="text-[11px] text-muted-foreground text-center max-w-[300px] leading-relaxed">
              {t("security.gate.note")}
            </p>
            <Button variant="ghost" size="sm" className="rounded-xl text-xs" onClick={cancel}>
              <LogOut className="w-3.5 h-3.5 mr-1.5" />
              {t("security.gate.signOut")}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
